// The account home: a greeting with the account's 7-day totals, one line per project for a chosen
// metric, a tile per project (status, big number, delta, "N× usual", sparkline), the week's top
// story lines across projects, new milestones, and the two ways in for a new account.
import { useState } from "react";
import { useConsole } from "../App.tsx";
import { Unusual, Delta } from "../charts/Delta.tsx";
import { Lines, Spark } from "../charts/Lines.tsx";
import { fmt } from "../fmt.ts";
import { useLoad } from "../data/hooks.ts";
import { homeStats, milestoneLabel, type HomeStats } from "../data/stats.ts";
import { projectHref } from "../router.ts";
import { projectColor } from "../shell/nav.tsx";
import { Card, Failed, Loading, ProjectPill, Stats } from "../ui.tsx";

const METRICS = [
  { id: "activeUsers", label: "Active users", format: fmt.int },
  { id: "requests", label: "Requests", format: fmt.compact },
  { id: "docWrites", label: "Writes", format: fmt.compact },
] as const;

const greeting = () => {
  const h = new Date().getHours();
  return h < 5 ? "Up late" : h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

export function Choices() {
  return (
    <div className="choices">
      <a className="choice" href="#/new/fresh"><strong>Starting fresh</strong><span className="muted">A new app. Pick a starter and your backend is built from it: schema, rules, sign-in. Then <span className="mono">npm create @estra</span> gives you the code.</span></a>
      <a className="choice" href="#/new/firebase"><strong>Moving from Firebase</strong><span className="muted">Draft a schema from your Firestore data, bring your rules, copy the data, and switch one host at a time.</span></a>
    </div>
  );
}

export function Home() {
  const { user, account, projects } = useConsole();
  const ids = (projects ?? []).map((p) => p.id);
  const stats = useLoad<HomeStats>(projects ? `home:${ids.join(",")}` : null, () => homeStats(user.uid, ids), 60_000);
  const [metric, setMetric] = useState<(typeof METRICS)[number]["id"]>("activeUsers");
  const first = String(account.name || "").split(" ")[0];
  if (!projects) return <div className="page"><Loading /></div>;
  if (!projects.length) {
    return (
      <div className="page medium">
        <div className="hello"><span className="kicker">Estra Cloud · free beta</span><h1>{first ? `Welcome, ${first}.` : "Welcome."} Let's build your <em>first backend</em>.</h1><p className="muted">Schema, rules, sign-in, realtime, files, email and push: one address, ready in minutes.</p></div>
        <Choices />
      </div>
    );
  }
  const s = stats.data;
  const m = METRICS.find((x) => x.id === metric)!;
  const series = (s?.projects ?? []).map((ps) => {
    const p = projects.find((x) => x.id === ps.id);
    return { id: ps.id, name: p?.name ?? ps.id, color: projectColor(ps.id, projects), values: (ps.series[metric] ?? []) as (number | null)[] };
  }).filter((x) => x.values.length);
  const unseen = (s?.milestones ?? []).filter((x) => !x.seen).slice(0, 3);
  return (
    <div className="page">
      <div className="hello">
        <span className="kicker">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</span>
        <h1>{greeting()}{first ? `, ${first}` : ""}.</h1>
        <p className="muted">{projects.length === 1 ? "Your project" : `Your ${projects.length} projects`} over the last 7 days.</p>
      </div>
      {unseen.map((x) => {
        const p = projects.find((pp) => pp.id === x.projectId);
        return <a key={`${x.projectId}${x.key}`} className="celebrate" href={projectHref(x.projectId!)} style={{ color: "inherit", textDecoration: "none" }}><span className="burst" aria-hidden="true" /><div><h3>{p?.name}: {milestoneLabel(x)}</h3><p className="small muted">Open the project to celebrate →</p></div></a>;
      })}
      {stats.error ? <Failed error={stats.error} retry={stats.reload} /> : !s ? <Loading /> : (
        <>
          <Stats items={[
            { label: "Requests", value: s.totals.requests ?? null, format: fmt.compact },
            { label: "Document reads", value: s.totals.docReads ?? null, format: fmt.compact },
            { label: "Active users", value: s.totals.activeUsers ?? null },
            { label: "Emails · pushes", value: (s.totals.emails ?? 0) + (s.totals.pushes ?? 0), note: `${fmt.int(s.totals.emails ?? 0)} emails, ${fmt.int(s.totals.pushes ?? 0)} pushes` },
          ]} />
          <section className="activity">
            <div className="activity-head">
              <span className="chart-title">{m.label} per project · 14 days</span>
              <div className="seg" role="tablist">{METRICS.map((x) => <button key={x.id} role="tab" aria-selected={x.id === metric} className={x.id === metric ? "on" : ""} onClick={() => setMetric(x.id)}>{x.label}</button>)}</div>
            </div>
            {series.some((x) => x.values.some((v) => (v ?? 0) > 0)) ? <Lines days={s.days} series={series} format={m.format} label={m.label} /> : <p className="muted center">{s.fallback && metric !== "requests" ? "Per-project activity starts with your backends' next update. Requests are shown meanwhile." : "No activity yet in the last 14 days."}</p>}
          </section>
        </>
      )}
      <div className="section-head"><h2>Projects</h2><a className="btn btn-small" href="#/new">New project</a></div>
      <div className="tiles">
        {projects.map((p) => {
          const ps = s?.projects.find((x) => x.id === p.id);
          const t = ps?.kpis[metric === "docWrites" ? "docWrites" : metric];
          const vals = (ps?.series[metric] ?? []) as number[];
          return (
            <a key={p.id} className="tile" href={projectHref(p.id)}>
              <span className="tile-name"><span className="dot" style={{ background: projectColor(p.id, projects) }} />{p.name}<ProjectPill p={p} /></span>
              <span className="tile-num">{t ? m.format(t.now) : "–"}<small>{m.label.toLowerCase()} · 7d</small></span>
              <Spark values={vals} color={projectColor(p.id, projects)} />
              <span className="tile-meta">
                <span className="row" style={{ gap: 6 }}>{t && <Delta now={t.now} prev={t.prev} good="up" format={m.format} label="the 7 days before" />}{t && <Unusual t={t} good="up" format={m.format} min={20} />}<span className="mono">{p.slug}</span></span>
                <span className="tile-go">Open →</span>
              </span>
            </a>
          );
        })}
        <a className="tile new" href="#/new"><span className="plus">+</span><span>New project</span></a>
      </div>
      {s && s.story.length > 0 && (
        <Card title="This week on Estra" className="" sub="The biggest changes across your projects.">
          <ul className="story">
            {s.story.slice(0, 5).map((l, i) => {
              const p = projects.find((x) => x.id === l.projectId);
              return <li key={i}><span className={`tone ${l.tone}`}>{l.tone === "good" ? "▲" : l.tone === "bad" ? "▼" : "•"}</span><span><strong>{p?.name}</strong> · {l.text}</span><a className="go" href={l.href}>Look →</a></li>;
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
